'use client'

import { isEmpty } from 'lodash-es'
import { useTranslations } from 'next-intl'

import { JoinType } from '@/entities/auth'
import { useRouter } from '@/shared/i18n'
import { useDialog, useSession } from '@/shared/lib'
import { AlertDialog, Button, Popover } from '@/shared/ui'

import { useSignUp } from '../api/sign-up'
import { convertFormValuesToSignUpGuest } from '../model/converter'
import { useSignUpGuestStore } from '../model/guest-sign-up'
import { useSignUpGuestCompleteStore } from '../model/guest-sign-up-complete'

const SUCCESS_DIALOG = 'SUCCESS_DIALOG'

type Props = {
  disabled: boolean
}

export const SignUpGuestButton = ({ disabled }: Props) => {
  const t = useTranslations()

  const session = useSession()
  const router = useRouter()
  const { isOpen, open, close } = useDialog()

  const { verification } = useSignUpGuestStore()
  const isPassportVerified =
    !isEmpty(verification.contactNumber) &&
    !isEmpty(verification.passportImageUrl)

  const actions = useSignUpGuestCompleteStore(state => state.actions)

  const { signUp } = useSignUp({
    joinType: JoinType.GUEST,
    onSuccess: async () => {
      actions.updateComplete({
        surveyAvailable: true,
        signUpCompleteTime: new Date().toISOString(),
        verificationType: isPassportVerified ? 'passport' : 'phone',
      })

      if (isPassportVerified) {
        open(SUCCESS_DIALOG)

        return
      }

      await session.update()

      router.push(`/sign-up/guest/complete`)
    },
  })

  const handleSignUp = () => {
    const signUpData = convertFormValuesToSignUpGuest()

    if (!signUpData) {
      return
    }

    signUp(signUpData)
  }

  const handleDialogConfirm = async () => {
    close(SUCCESS_DIALOG)

    await session.update()

    router.push(`/sign-up/guest/complete`)
  }

  const closeDialog = () => {
    close(SUCCESS_DIALOG)
  }

  return (
    <>
      <Popover.Root open={!disabled}>
        <Popover.Anchor asChild>
          <Button
            disabled={disabled}
            size="large"
            fullWidth
            onClick={handleSignUp}
          >
            {t('verification.button.complete_signup')}
          </Button>
        </Popover.Anchor>
        <Popover.Content
          color="purple"
          sideOffset={8}
          closeButton={false}
          className="text-center whitespace-break-spaces"
        >
          {isPassportVerified
            ? t('passport_verification.tooltip.start_review')
            : t('verification.tooltip.complete_signup')}
        </Popover.Content>
      </Popover.Root>
      <AlertDialog
        isOpen={isOpen(SUCCESS_DIALOG)}
        onOpenChange={closeDialog}
        title={t('passport_verification.dialog.signup_completed.title')}
        content={t('passport_verification.dialog.signup_completed.description')}
        onConfirm={handleDialogConfirm}
        preventOutsideClick
      />
    </>
  )
}
